import { useEffect, useState, RefObject } from 'react';

type SelectionState = {
  start: number;
  end: number;
  dir: 'forward' | 'backward' | 'none';
};

/**
 * Hook for controlling the selection in a textarea element.
 *
 * This hook moves control of the selection in the textarea element from the
 * browser and into React. This makes it easier to update the selection without
 * artifacts in the caret position related to the browser's selection handling.
 *
 * @param textAreaRef A ref to the textarea element to control selection in.
 * @returns A `setCaretPosition` function that can be used to set the caret position in the textarea.
 */
export const useTextAreaSelection = (
  textAreaRef: RefObject<HTMLTextAreaElement | null>
) => {
  const [selection, setSelection] = useState<SelectionState | undefined>();

  useEffect(() => {
    const listener = (e: Event) => {
      const target = e.currentTarget as HTMLTextAreaElement;
      setSelection({
        start: target.selectionStart,
        end: target.selectionEnd,
        dir: target.selectionDirection,
      });
    };
    const el = textAreaRef.current;
    el?.addEventListener('select', listener);
    return () => el?.removeEventListener('select', listener);
  }, [textAreaRef]);

  useEffect(() => {
    textAreaRef.current?.setSelectionRange(
      selection?.start ?? -1,
      selection?.end ?? -1,
      selection?.dir ?? 'none'
    );
  }, [selection, textAreaRef]);

  const setCaretPosition = (pos: number) =>
    setSelection({
      start: pos,
      end: pos,
      dir: 'none',
    });

  return setCaretPosition;
};
